import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { eOrderType, eOrderTypeGroup, instantValidationThrottle, LienDetailsUIConfiguration, UIConfigurationService } from 'reg-hub-common';
import { Order, Utilities, ValidationProblem } from 'reg-hub-common';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-term',
    templateUrl: './term.component.html',
    styleUrls: ['./term.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TermComponent implements OnInit, OnDestroy, OnChanges {
  private readonly transloco = inject(TranslocoService);
  @Input() isDisabled: boolean = false;
  @Input() order!: Order;
  @Input() errors$!: Observable<ValidationProblem[] | undefined>;
  @Input() uiConfiguration: LienDetailsUIConfiguration | undefined;
  @Output() termFormValueChangedEvent = new EventEmitter<any>();

  termDetails!: FormGroup;

  private onDestroy$ = new Subject<void>();

  protected termError$ = new BehaviorSubject<string | null | undefined>(null);
  protected expiryDateError$ = new BehaviorSubject<string | null | undefined>(null);

  protected selectedTermType: number = -1;

  protected termInYearsSelection: number = 0;
  protected expiryDateSelection: number = 1;
  protected infiniteSelection: number = 2;

  protected termOptions: { value: number, viewValue: string }[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private uiConfigurationservice: UIConfigurationService) { }

  ngOnInit(): void {
    if(!this.uiConfiguration) {
      return;
    }

    this.selectedTermType = this.getInitialTermSelection();

    this.termDetails = this.formBuilder.group({
      selectedTermType: [this.selectedTermType]
    }) as FormGroup;

    if(this.uiConfiguration.showTerm) {
      this.termDetails.addControl('term', this.formBuilder.control(this.order.lien?.term));
    }

    if (this.uiConfiguration.showExpiryDate) {
      this.termDetails.addControl(
        'expiryDate',
        this.formBuilder.control(this.order.lien?.expiryDate, [
          Validators.required,
          Utilities.validDateValidator(),
        ])
      );

      // Subscribe to the status changes of the expiryDate control
      this.termDetails.statusChanges.pipe(takeUntil(this.onDestroy$))
        .subscribe((status) => {
          if (status === 'INVALID') {
            const errors = this.termDetails.get('expiryDate')?.errors;
            if (errors?.['required']) {
              this.expiryDateError$.next(this.transloco.translate('orders.expiryDateIsRequired'));
            } else if (errors?.['invalidDate']) {
              this.expiryDateError$.next(this.transloco.translate('orders.invalidExpiryDate'));
            } else {
              this.expiryDateError$.next(this.transloco.translate('orders.invalidExpiryDate'));
            }
          } else {
            this.expiryDateError$.next(null);
          }
        });
    }

    if(this.uiConfiguration.showInfinityTerm) {
      this.termDetails.addControl('isInfiniteTerm', this.formBuilder.control(this.order.lien?.isInfiniteTerm));
    }

    this.termOptions = this.getTermOptions();

    this.termDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.termFormValueChangedEvent.emit(this.termDetails.value));
    this.errors$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(errors => this.pushErrors(errors));
    this.uiConfigurationservice.uiConfiguration$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(config => {
        if (this.order.orderTypeID != eOrderType.renewal) {
          this.uiConfiguration = config?.lienDetailsConfiguration ?? this.uiConfiguration;
        } else {
          this.uiConfiguration = config?.renewalDetailsConfiguration ?? this.uiConfiguration;
        }
        this.termOptions = this.getTermOptions();
      });

    if(this.isDisabled) {
      this.termDetails.disable();
    }
    
    this.termFormValueChangedEvent.emit(this.termDetails.value)
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const term = this.termDetails?.get('term');

    if(term?.value > (this.uiConfiguration?.maxTerm ?? 0)) {
      term?.reset();
    }
  }

  getTermOptions(): { value: number, viewValue: string }[] {
    const lengths: { value: number, viewValue: string }[] = [];
    
    if(this.uiConfiguration?.minTerm && this.uiConfiguration.maxTerm) {
      for(let i = this.uiConfiguration.minTerm; i <= this.uiConfiguration.maxTerm; i++) {
        lengths.push({ value: i, viewValue: this.transloco.translate(i > 1 ? 'orders.termNYears' : 'orders.termOneYear', { n: i }) });
      }
    }

    return lengths;
  }

  getInitialTermSelection(): number {
    if(!this.uiConfiguration) {
      return 0;
    }

    if(this.order.orderType.orderTypeGroupID === eOrderTypeGroup.BasicPPSARegistration &&
      this.order.referenceRegistrationInformation?.originalExpiryDate &&
      this.uiConfiguration.showExpiryDate) {
      return this.expiryDateSelection;
    } else if(this.order.lien?.expiryDate &&
      this.uiConfiguration.showExpiryDate) {
      return this.expiryDateSelection;
    } else if(this.order.lien?.term &&
      this.uiConfiguration.showTerm) {
      return this.termInYearsSelection;
    } else if(this.order.lien?.isInfiniteTerm &&
      this.uiConfiguration.showInfinityTerm){
      return this.infiniteSelection;
    } else {
      if(this.uiConfiguration.showTerm) {
        return this.termInYearsSelection;
      } else if(this.uiConfiguration.showExpiryDate) {
        return this.expiryDateSelection;
      } else if(this.uiConfiguration.showInfinityTerm) {
        return this.infiniteSelection;
      } else {
        return -1;
      }
    }
  }

  onTermSelectionChange(event: MatRadioChange): void {
    this.selectedTermType = event.value;

    if (event.value === this.termInYearsSelection) {
      this.order.lien!.expiryDate = null;
      this.order.lien!.isInfiniteTerm = false;

      this.termDetails?.get('expiryDate')?.setValue(null, { emitEvent: false });
      this.termDetails?.get('isInfiniteTerm')?.setValue(false);
    } else if (event.value === this.expiryDateSelection) {
      this.order.lien!.term = null;
      this.order.lien!.isInfiniteTerm = false;

      this.termDetails?.get('term')?.setValue(null, { emitEvent: false });
      this.termDetails?.get('isInfiniteTerm')?.setValue(false);
    } else {
      this.order.lien!.term = null;
      this.order.lien!.expiryDate = null;
      this.order.lien!.isInfiniteTerm = true;

      this.termDetails?.get('term')?.setValue(null, { emitEvent: false });
      this.termDetails?.get('expiryDate')?.setValue(null, { emitEvent: false });
      this.termDetails?.get('isInfiniteTerm')?.setValue(true);
    }
  }

  public pushErrors(errors: ValidationProblem[] | undefined): void {
    if(!errors) {
      return;
    }

    const termError = errors.find(error => error.path.includes('/term'))?.userFriendlyMessage;
    this.termError$.next(termError);

    const expiryDateError = errors.find(error => 
      (!error.path.includes('referenceregistrationinformation') && 
      error.path.includes('/expirydate')) ||
      error.path.endsWith('/term'))?.userFriendlyMessage;
    this.expiryDateError$.next(expiryDateError);
  }
}
